import React, { useMemo } from "react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { useGetPlanDetail, usePublishPlan, useContract, useGetPlanDetailByTab } from "../../../hooks";
import { useParams } from "react-router-dom";
import { Layers, Calendar, Clock, Box } from "lucide-react";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

interface CreateReviewProps {
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const CreateReview: React.FC<CreateReviewProps> = ({
  data,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
}) => {
  const { planId } = useParams();
  const { data: planDetail } = useGetPlanDetail(planId);
  const { mutateAsync: publishPlan, isPending: isPublishing } = usePublishPlan();

  const planData = planDetail?.result || {};

  const categories = useMemo(() => {
    return planData.categories || planData.overview?.eligibility?.categories || [];
  }, [planData]);

  const subCategories = useMemo(() => {
    return planData.subCategories || planData.sub_categories || planData.overview?.eligibility?.subCategories || planData.overview?.eligibility?.sub_categories || [];
  }, [planData]);

  const categoryCodes = useMemo(() => {
    return categories.map((c: any) => c.code || c.id || c.value);
  }, [categories]);

  const subCategoryCodes = useMemo(() => {
    return subCategories.map((s: any) => s.code || s.id || s.value);
  }, [subCategories]);

  const { data: contractsResult } = useContract(categoryCodes, subCategoryCodes);

  const attachedContracts = useMemo(() => {
    const ids = planData.contractIds || planData.contract_ids || [];
    if (!Array.isArray(contractsResult)) return [];
    return contractsResult.filter((c: any) => ids.includes(Number(c.id || c.contract_id || c.key || c.code)));
  }, [planData, contractsResult]);

  const contractsList = useMemo(() => {
    return planData.contracts || planData.attachedContracts || planData.attached_contracts || attachedContracts || [];
  }, [planData, attachedContracts]);

  const { data: planDetailResponseRates } = useGetPlanDetailByTab(planId || "", "rates");

  const SERVICE_COLORS: Record<string, string> = {
    gas: "bg-orange-500",
    water: "bg-blue-500",
    electricity: "bg-yellow-500",
    sewer: "bg-teal-500",
    "ev charging": "bg-green-500"
  };

  const ratesData = useMemo(() => {
    if (!planDetailResponseRates?.result) return [];

    const ratesByService = planDetailResponseRates.result.ratesByService || {};
    const conditionalRules = planDetailResponseRates.result.conditionalRateRules || {};
    
    // Get all unique services
    const allServices = Array.from(new Set([
      ...Object.keys(ratesByService),
      ...Object.keys(conditionalRules)
    ]));

    return allServices.map(service => {
      const rates = [];
      const condRule = conditionalRules[service];
      
      if (condRule?.enabled) {
        if (condRule.belowRate) {
          rates.push({
            id: condRule.belowRate.rateId || "N/A",
            name: condRule.belowRate.rateName || "N/A",
            type: condRule.belowRate.rateTypeDisplay ||  "N/A",
            code: condRule.belowRate.rateCode || "N/A",
            description: condRule.belowRate.description || "N/A",
          });
        }
        if (condRule.aboveRate) {
          rates.push({
            id: condRule.aboveRate.rateId || "N/A",
            name: condRule.aboveRate.rateName || "N/A",
            type: condRule.aboveRate.rateTypeDisplay ||  "N/A",
            code: condRule.aboveRate.rateCode || "N/A",
            description: condRule.aboveRate.description || "N/A",
          });
        }
      } else if (ratesByService[service]) {
        const rate = ratesByService[service];
        rates.push({
          id: rate.rateId ||  "N/A",
          name: rate.rateName|| "N/A",
          type: rate.rateTypeDisplay ||  "N/A",
          code: rate.rateCode || "N/A",
          description: rate.description || "N/A",
        });
      }

      return {
        service: service.charAt(0).toUpperCase() + service.slice(1),
        serviceColor: SERVICE_COLORS[service.toLowerCase()] || "bg-purple-500",
        rates
      };
    });
  }, [planDetailResponseRates]);

  const handleFinish = async () => {
    if (isLastStep && planId) {
      try {
        await publishPlan({
          planID: planId,
          payload: {
          }
        });
        if (onNext) onNext();
      } catch (error) {
        console.error("Failed to publish plan", error);
      }
    } else if (onNext) {
      onNext();
    }
  };

  return (
    <div className="w-full  space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold mb-1 text-gray-900">
          Review & Pricing Summary
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Confirm your configuration. You can save a draft, schedule activation, submit for approval, or activate now.
        </p>
      </div>

      {/* Basic Info Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm relative overflow-hidden flex flex-col mt-6">
        <div className="p-6 flex flex-col md:flex-row gap-6 items-start">
          {/* Icon */}
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 border border-blue-100">
            <Layers className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
          </div>
          
          {/* Text Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <span className="text-xs font-bold text-gray-400 tracking-wider uppercase">
                {planData.planCode || 'N/A'}
              </span>
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-1.5">
              {planData.planName || 'Plan Name'}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed max-w-2xl">
              {planData.description || 'No description provided.'}
            </p>
          </div>
        </div>

        {/* Metadata Footer Grid */}
        <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-6">

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-gray-100 shadow-sm shrink-0">
              <Calendar className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[12px] font-semibold text-gray-500 tracking-wider">
                Effective From
              </div>
              <div className="text-sm font-semibold text-gray-800">
                {planData.effectiveDate || 'N/A'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-gray-100 shadow-sm shrink-0">
              <Clock className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[12px] font-semibold text-gray-500 tracking-wider">
                Validate To
              </div>
              <div className="text-sm font-semibold text-gray-800">
                {planData.endDate || 'Ongoing'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-gray-100 shadow-sm shrink-0">
              <Box className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[12px] font-semibold text-gray-500 tracking-wider">
                Billing Cycle
              </div>
              <div className="text-sm font-semibold text-gray-800">
                {planData.overview?.planConfiguration?.billingCycleDisplay || planData.billingCycleDisplay || planData.billingCycle || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category, Subcategory, & Contracts Card */}
      <div className="border border-gray-200 rounded-lg bg-white p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-[12px] font-semibold text-gray-500 tracking-wider mb-2">
              Categories
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.length > 0 ? (
                categories.map((cat: any) => (
                  <Badge key={cat.code || cat.id} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    {cat.name}
                  </Badge>
                ))
              ) : (
                <span className="text-xs text-gray-400">None specified</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-gray-500 tracking-wider mb-2">
              Sub-categories
            </div>
            <div className="flex flex-wrap gap-2">
              {subCategories.length > 0 ? (
                subCategories.map((sub: any) => (
                  <Badge key={sub.code || sub.id} variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                    {sub.name}
                  </Badge>
                ))
              ) : (
                <span className="text-xs text-gray-400">None specified</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-gray-500 tracking-wider mb-2">
              Attached Contracts
            </div>
            <div className="flex flex-wrap gap-2">
              {contractsList.length > 0 ? (
                contractsList.map((contract: any) => (
                  <Badge key={contract.id || contract.code} variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
                    {contract.name}
                  </Badge>
                ))
              ) : (
                <span className="text-xs text-gray-400">None specified</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rates by Service */}
      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
          <h3 className="text-sm font-semibold text-gray-800">Rates by Service</h3>
        </div>
        <div className="p-5 space-y-6">
          {ratesData.length > 0 ? (
            ratesData.map((serviceGroup) => (
              <div key={serviceGroup.service} className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${serviceGroup.serviceColor}`}></span>
                  <h4 className="font-semibold text-sm text-gray-900 capitalize">{serviceGroup.service}</h4>
                </div>
                
                <div className={serviceGroup.rates.length === 1 ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
                  {serviceGroup.rates.map((rate) => (
                    <div key={rate.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-start bg-white hover:shadow-sm transition-all duration-200">
                      <div>
                        <div className="font-semibold text-sm text-gray-900 mb-0.5">
                          {rate.name}
                        </div>
                        <div className="text-xs text-gray-400 font-mono mb-2 uppercase tracking-wide">
                          {rate.code}
                        </div>
                        <p className="text-xs text-gray-500">
                          {rate.description}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 shrink-0 ml-4">
                        {rate.type}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-400 text-center py-6">No rates assigned yet.</div>
          )}
        </div>
      </div>
      {/* Footer Actions */}
      <div className="flex items-center justify-between mt-8 border-t pt-6">
        <div>
          {!isFirstStep && onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Button 
            type="button" 
            onClick={handleFinish} 
            disabled={isPublishing}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isPublishing ? "Publishing..." : isLastStep ? "Finish" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateReview;
